import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Autocomplete from "@mui/material/Autocomplete";
import { InputBaseComponentProps } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { green, lightBlue } from "@mui/material/colors";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import CurrencyInput from "_shared/money/currencies/_views/currency-input";
import { Currency as CurrencyType } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import React, { forwardRef } from "react";
import TextChange from "work/proposals/redlining/__diff/text-change";
import FeeRedline, { FeeType } from "work/proposals/redlining/fee-schedule-redline/fee-redline/fee-redline";

const DiffContainer = styled("div")(() => ({
  width: "100%",
}));
const DiffResult = styled("span", {
  shouldForwardProp: (prop) => !["isDeferred", "isNewlyAdded", "change", "resolved", "acceptedResolved"].includes(prop.toString()),
})<{
  isDeferred?: boolean;
  isNewlyAdded?: boolean;
  change?: TextChange;
  resolved?: boolean;
  acceptedResolved?: boolean;
}>(({ isDeferred, isNewlyAdded, change, resolved, acceptedResolved, theme }) => ({
  backgroundColor: (function () {
    if (resolved) return "unset";
    if (change?.isAdded) return green[300];
  })(),
  color: (function () {
    if (change?.isRemoved && acceptedResolved) return theme.palette.text.disabled;
    if (isNewlyAdded) return lightBlue[700];
    if (isDeferred && !isNewlyAdded) return theme.palette.warning.main;
    return theme.palette.text.primary;
  })(),
  textDecoration: (function () {
    if (change?.isRemoved) return "line-through";
  })(),
  textDecorationColor: (function () {
    if (change?.isRemoved && acceptedResolved) return theme.palette.text.primary;
    if (change?.isRemoved && resolved) return lightBlue[700];
    if (change?.isRemoved) return theme.palette.error.main;
  })(),
  textDecorationThickness: '2px',
  '&:hover': {
    textDecorationColor: (function() { 
      let color = "initial";
      if (change?.isRemoved && resolved) color = lightBlue[700];
      if (change?.isRemoved && !resolved) color = theme.palette.error.main;
      return `oklch(from ${color} l c h / 0.25)`
    }())
  },
}));
const AcceptedRemovedFee = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
  textDecoration: "line-through",
  textDecorationColor: theme.palette.text.primary,
  textDecorationThickness: "2px",
}));
const TextContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
}));
const InputContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  "&.MuiInputBase-input": {
    height: "unset",
  },
}));
const ChangeContainer = styled("div")(() => ({
  fontSize: "0.875rem",
}));
const DiffHeader = styled(Typography)(({ theme }) => ({
  color: lightBlue[500],
  display: "inline-block",
  fontSize: "0.75rem",
  lineHeight: 1,
  minWidth: theme.spacing(13.5),
}));
const PerSymbol = styled("span")(({ theme }) => ({
  fontSize: "1.5em",
  margin: theme.spacing(0, 1),
}));
const FeeRate = styled("span")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));
const UnitAutocomplete = styled(Autocomplete)(({ theme }) => ({
  flex: "1 1 10rem",
  minWidth: "8rem",
  maxWidth: "16rem",
  "& .MuiInputBase-root": {
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "none",
  },
}));
const FixedFeeLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
}));
const FeeInput = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(1),
}));
const CurrencySelect = styled(CurrencyInput)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
  }
}));
const UNIT_OPTIONS = ["Hour", "Day", "Week", "Month", "Year", "Each"] as const;

export interface DiffFeeProps extends InputBaseComponentProps {
  className?: string;
  feeRedline: FeeRedline | null;
  isNewlyAdded: boolean;
  acceptedResolved?: boolean;
  acceptedResolvedRemoval?: boolean;
  acceptedRemovedLabel?: string;
  showTextInput?: boolean;
  onFeeInputChange?: (fee: Fee) => void;
  onTextInputSubmit?: () => void;
  onTextInputCancel?: () => void;
}

const DiffFee = forwardRef<HTMLInputElement, DiffFeeProps>(
  function DiffFee(props, ref) {
    const {
      className,
      feeRedline,
      isNewlyAdded,
      acceptedResolved,
      acceptedResolvedRemoval,
      acceptedRemovedLabel,
      showTextInput,
      onClick,
      onFeeInputChange,
      onTextInputSubmit,
      onTextInputCancel
    } = props;

    const [currentFeeRate, setCurrentFeeRate] = React.useState<Money | undefined>(
      feeRedline?.currentEntry?.rate ?? feeRedline?.revisedEntry?.rate ?? feeRedline?.originalEntry?.rate
    );
    const [currentFeeUnit, setCurrentFeeUnit] = React.useState<string>(
      feeRedline?.currentEntry?.unit ?? feeRedline?.revisedEntry?.unit ?? feeRedline?.originalEntry?.unit ?? ""
    );
    const [isFixedFee, setIsFixedFee] = React.useState<boolean>(
      (feeRedline?.currentEntry?.unit ?? feeRedline?.revisedEntry?.unit ?? feeRedline?.originalEntry?.unit) === null
    );

    const handleFixedFeeChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setIsFixedFee((prevValue) => !prevValue);
      let updatedFee: Fee;
      if (event.target.checked) {
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: null });
      } else {
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: currentFeeUnit });
      }
      onFeeInputChange?.(updatedFee);
    };

    const handleFeeUnitChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      let updatedFee: Fee;
      if (isFixedFee) {
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: null });
      } else {
        setCurrentFeeUnit(event.target.value);
        updatedFee = Fee.create({ rate: currentFeeRate ?? Money.create({ amount: 0, currency: CurrencyType.USD }), unit: event.target.value });
      }

      onFeeInputChange?.(updatedFee);
    };

    const handleFeeRateChange = (currency: CurrencyType, amount: number) => {
      setCurrentFeeRate(Money.create({ amount, currency: currency ?? CurrencyType.USD }));
      const updatedFee = Fee.create({
        rate: new Money(amount, currency ?? CurrencyType.USD),
        unit: isFixedFee ? null : currentFeeUnit,
      });
      onFeeInputChange?.(updatedFee);
    };

    const getChangeKey = (change: TextChange): string => {
      let changeType: string;
      if (change.isAdded) {
        changeType = "added";
      } else if (change.isRemoved) {
        changeType = "removed";
      } else {
        changeType = "unchanged";
      }
      return `${change?.diff.value}_${changeType}`;
    };

    const getFeeLabel = (fee: FeeType | undefined): string => {
      if (fee === null) return "Deferred";
      if (fee === false || fee === undefined) return "";
      return fee.toString();
    };

    const getPreviouslyDisplayedFee = (): FeeType | undefined => {
      if (feeRedline?.currentEntry !== undefined && feeRedline.currentEntry !== false) return feeRedline.currentEntry;
      if (feeRedline?.revisedEntry !== false) return feeRedline?.revisedEntry;
      return feeRedline?.originalEntry;
    };

    const canSubmit: boolean =
      (currentFeeRate && isFixedFee) ||
      (currentFeeRate && currentFeeUnit && !isFixedFee) ||
      false;

    return (
      <TextContainer>
        <input ref={ref} type="hidden" />
        <InputContainer className={className}>
          {!showTextInput && (
            <DiffContainer onClick={onClick}>
              {acceptedResolvedRemoval && !feeRedline?.textChanges.length ? (
                <AcceptedRemovedFee>{acceptedRemovedLabel}</AcceptedRemovedFee>
              ) : feeRedline?.textChanges.map((change: TextChange) => {
                return (
                  <DiffResult
                    key={getChangeKey(change)}
                    isDeferred={feeRedline.currentEntry === null}
                    isNewlyAdded={isNewlyAdded}
                    change={change}
                    resolved={feeRedline.isResolved}
                    acceptedResolved={acceptedResolved}
                  >
                    {change?.diff.value}
                  </DiffResult>
                );
              })}
            </DiffContainer>
          )}
          {showTextInput && (
            <>
              <ChangeContainer>
                <DiffHeader variant="overline">previously:</DiffHeader>
                {getFeeLabel(getPreviouslyDisplayedFee())}
              </ChangeContainer>
              <ChangeContainer>
                <DiffHeader variant="overline">your changes:</DiffHeader>
                {feeRedline?.textChanges.map((change: TextChange) => {
                  return (
                    <DiffResult
                      isDeferred={feeRedline?.currentEntry === null}
                      isNewlyAdded={isNewlyAdded}
                      key={getChangeKey(change)}
                      change={change}
                      resolved
                    >
                      {change?.diff.value}
                    </DiffResult>
                  );
                })}
              </ChangeContainer>
              <FeeInput>
                <FeeRate>
                  <CurrencySelect
                    allowNegative={false}
                    size="small"
                    label="Amount"
                    variant="standard"
                    value={currentFeeRate}
                    onChange={handleFeeRateChange}
                    onBlurCapture={() => {
                      if (isFixedFee) onTextInputSubmit?.();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === KeyboardKey.Enter && canSubmit) {
                        onTextInputSubmit?.();
                      }
                      if (event.key === KeyboardKey.Escape) {
                        onTextInputCancel?.();
                      }
                    }}
                  />
                  {!isFixedFee && (
                    <>
                      <PerSymbol>/</PerSymbol>
                      <UnitAutocomplete
                        freeSolo
                        options={UNIT_OPTIONS}
                        inputValue={currentFeeUnit}
                        disabled={!currentFeeRate?.amount}
                        onInputChange={(_event, value) => {
                          handleFeeUnitChange({
                            target: { value },
                          } as React.ChangeEvent<HTMLInputElement>);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Unit"
                            size="small"
                            variant="standard"
                            onBlurCapture={() => {
                              if (!currentFeeUnit) return;
                              onTextInputSubmit?.();
                            }}
                            onKeyDown={(event) => {
                              if (event.key === KeyboardKey.Enter && canSubmit) {
                                onTextInputSubmit?.();
                              }
                              if (event.key === KeyboardKey.Escape) {
                                onTextInputCancel?.();
                              }
                            }}
                          />
                        )}
                      />
                    </>
                  )}
                </FeeRate>
                <FixedFeeLabel
                  label="Fixed"
                  labelPlacement="end"
                  control={
                    <Checkbox
                      size="small"
                      edge="start"
                      color="primary"
                      icon={<CheckBoxOutlineBlankIcon />}
                      checked={isFixedFee}
                      onChange={handleFixedFeeChange}
                      onKeyDown={(event) => {
                        if (event.key === KeyboardKey.Enter && canSubmit) {
                          onTextInputSubmit?.();
                        }
                        if (event.key === KeyboardKey.Escape) {
                          onTextInputCancel?.();
                        }
                      }}
                    />
                  }
                />
              </FeeInput>
            </>
          )}
        </InputContainer>
      </TextContainer>
    );
  }
);

export default DiffFee;
